import { DateTime } from 'luxon';

import { uuid } from '../../../../helpers/helpers';
import { getHours } from './TimeTrackingGridPreloadSectionParser';

export const getTaskISODate = (task) => {
  const { date } = task;
  if (!date || typeof date !== 'string') return null;
  const dt = DateTime.fromISO(date);
  return dt.isValid ? date : null;
};

export function normalizeCostcodeId(raw) {
  if (!raw) return null;
  const parts = String(raw).split('.');
  return parts.length === 2 ? parts[1] : raw;
}

function resolveTaskDataName(id, taskDataMap, defaultValue) {
  return taskDataMap[id]?.name || defaultValue;
}

function addToExistingHours(existingHours, newHours) {
  return (Number.parseFloat(existingHours) || 0) + (Number.parseFloat(newHours) || 0);
}

/** Numbers for grid cells (TimeTrackingGridCell expects numbers) */
function convertToNumber(value) {
  return Number(value) || 0;
}

function applyEquipmentGridValue(value, ctx, state) {
  const {
    task,
    costcodeId,
    costcodeName,
    costcodeCode,
    phaseNameForTask,
    projectNameForTask,
    projectNumberForTask,
    divisionCostcodeMap,
    divisionEquipmentMap,
    checkAndCreateColumn,
  } = ctx;

  const { equipmentId } = value;
  if (!equipmentId || !divisionEquipmentMap[equipmentId]) return state;

  const rowHours = Number.parseFloat(value.hours) || 0;
  if (rowHours <= 0) return state;

  const rowId = checkAndCreateColumn({
    costcodeId: (costcodeId && divisionCostcodeMap[costcodeId]) ? costcodeId : null,
    phaseId: task.phaseId ?? null,
    projectId: task.projectId ?? null,
    costcodeName,
    costcodeCode,
    phaseName: phaseNameForTask,
    projectName: projectNameForTask,
    projectNumber: projectNumberForTask,
  });

  const { valueMap, values } = state;
  const existingRow = valueMap[equipmentId];
  if (existingRow) {
    const existingCell = existingRow[rowId] || {};
    const newHours = addToExistingHours(rowHours, existingCell.hours);
    const updatedRow = {
      ...existingRow,
      [rowId]: {
        hours: convertToNumber(newHours),
        otHours: convertToNumber(existingCell.otHours),
        doubleOTHours: convertToNumber(existingCell.doubleOTHours),
      },
    };
    return {
      valueMap: { ...valueMap, [equipmentId]: updatedRow },
      values: values.map((row) => (row === existingRow ? updatedRow : row)),
    };
  }

  const newValue = {
    ...divisionEquipmentMap[equipmentId],
    isPreloaded: true,
    sourceForm: 'Time card',
    [rowId]: {
      hours: convertToNumber(rowHours),
      otHours: 0,
      doubleOTHours: 0,
    },
    type: 'equipment',
  };
  return {
    values: [...values, newValue],
    valueMap: { ...valueMap, [equipmentId]: newValue },
  };
}

function processEquipmentFieldForTask(field, ctx, state) {
  const { response } = field ?? {};
  if (!response) return state;
  const { values = [], dataType } = response;
  if (dataType !== 'Equipment') return state;
  let next = state;
  for (const v of values) {
    next = applyEquipmentGridValue(v, ctx, next);
  }
  return next;
}

function mergeEquipmentRowsFromCustomData(data, ctx, state) {
  if (!Array.isArray(data)) return state;
  let next = state;
  for (const step of data) {
    const { fields = [] } = step;
    for (const field of fields) {
      next = processEquipmentFieldForTask(field, ctx, next);
    }
  }
  return next;
}

/**
 * Maps preloaded tasks (same shape as /tasks/get) into time tracking grid columns and user rows.
 * Equipment: table rows from time-card custom fields.
 */
export function buildTimeTrackingGridFromTasks({
  tasks = [],
  gridDateIso,
  divisionUserMap,
  divisionCostcodeMap,
  divisionEquipmentMap = {},
  taskCustomDataMap = {},
  projectIdMap = {},
  phaseIdMap = {},
  existingColumns = [],
  existingValues = [],
}) {
  const relevantColumns = [...existingColumns];
  let relevantValues = [...existingValues];
  const costcodePhaseProjectMap = {};

  existingColumns.forEach((column) => {
    const {
      costcode: { id: costcodeId } = {},
      phase: { id: phaseId } = {},
      project: { id: projectId } = {},
    } = column;
    costcodePhaseProjectMap[`${costcodeId || ''}-${phaseId || ''}-${projectId || ''}`] = column.rowId;
  });

  let relevantValueMap = {};
  relevantValues.forEach((row) => {
    if (row?.id) relevantValueMap[row.id] = row;
  });

  const ensureUnassignedColumn = () => {
    const key = '--';
    if (!costcodePhaseProjectMap[key]) {
      costcodePhaseProjectMap[key] = 'unassigned';
      relevantColumns.push({
        rowId: 'unassigned',
        costcode: {
          fullText: 'Unassigned',
          id: null,
        },
        phase: {
          name: null,
          id: null,
        },
        project: {
          name: null,
          id: null,
        },
      });
    }
    return costcodePhaseProjectMap[key];
  };

  const checkAndCreateColumn = ({
    costcodeId,
    phaseId,
    projectId,
    costcodeName,
    costcodeCode,
    phaseName,
    projectName,
    projectNumber,
  }) => {
    // Only create the unassigned column when an entry has no project and no cost code
    if (!costcodeId && !projectId) {
      return ensureUnassignedColumn();
    }

    const costcodePhaseProject = `${costcodeId || ''}-${phaseId || ''}-${projectId || ''}`;

    if (!costcodePhaseProjectMap[costcodePhaseProject]) {
      const generatedRowId = uuid();
      costcodePhaseProjectMap[costcodePhaseProject] = generatedRowId;
      relevantColumns.push({
        rowId: generatedRowId,
        costcode: {
          fullText: costcodeName ? `${costcodeCode} ${costcodeName}` : 'Unassigned',
          id: costcodeId,
        },
        phase: {
          name: phaseName,
          id: phaseId,
        },
        project: {
          name: projectName,
          id: projectId,
          number: projectNumber,
        },
      });
    }

    return costcodePhaseProjectMap[costcodePhaseProject];
  };

  tasks.forEach((task) => {
    const taskDateIso = getTaskISODate(task);
    if (gridDateIso && taskDateIso && taskDateIso !== gridDateIso) return;

    const customPayload = taskCustomDataMap[task.id];
    const data = customPayload?.data;
    const phaseNameForTask = resolveTaskDataName(task.phaseId, phaseIdMap, 'Unphased');
    const projectNameForTask = resolveTaskDataName(task.projectId, projectIdMap, 'Unassigned');
    const projectNumberForTask = projectIdMap[task.projectId]?.number ?? null;

    const costcodeIdRaw = task.costcodeId;
    const costcodeId = normalizeCostcodeId(costcodeIdRaw) ?? costcodeIdRaw;

    const cc = costcodeId ? divisionCostcodeMap[costcodeId] : null;
    const costcodeName = cc?.name ?? task.costcode_name ?? task.costcodeName ?? null;
    const costcodeCode = cc?.code ?? task.costcode_code ?? task.costcodeCode ?? null;

    const equipmentRows = mergeEquipmentRowsFromCustomData(data, {
      task,
      costcodeId,
      costcodeName,
      costcodeCode,
      phaseNameForTask,
      projectNameForTask,
      projectNumberForTask,
      divisionCostcodeMap,
      divisionEquipmentMap,
      checkAndCreateColumn,
    }, {
      values: relevantValues,
      valueMap: { ...relevantValueMap },
    });
    relevantValues = equipmentRows.values;
    relevantValueMap = equipmentRows.valueMap;

    const { userId } = task;
    if (!userId || !divisionUserMap[userId]) return;

    const rowId = checkAndCreateColumn({
      costcodeId,
      phaseId: task.phaseId ?? null,
      projectId: task.projectId ?? null,
      costcodeName,
      costcodeCode,
      phaseName: phaseNameForTask,
      projectName: projectNameForTask,
      projectNumber: projectNumberForTask,
    });

    const {
      totalRegHours,
      totalOtHours,
      totalDoubleOThours,
    } = getHours(task, 'timeTrackingGridFromTasks');

    const existingRow = relevantValueMap[userId];
    if (existingRow) {
      const existingCell = existingRow[rowId] || {};
      const newHours = addToExistingHours(totalRegHours, existingCell.hours);
      const newOtHours = addToExistingHours(totalOtHours, existingCell.otHours);
      const newDoubleOThours = addToExistingHours(totalDoubleOThours, existingCell.doubleOTHours);

      const hadTask = !!existingCell.taskId;
      const sameTask = existingCell.taskId === task.id;

      existingRow[rowId] = {
        hours: convertToNumber(newHours),
        otHours: convertToNumber(newOtHours),
        doubleOTHours: convertToNumber(newDoubleOThours),
        taskId: (hadTask && !sameTask) ? undefined : (task.id ?? existingCell.taskId),
      };
    } else {
      const newValue = {
        ...divisionUserMap[userId],
        isPreloaded: true,
        [rowId]: {
          hours: convertToNumber(totalRegHours),
          otHours: convertToNumber(totalOtHours),
          doubleOTHours: convertToNumber(totalDoubleOThours),
          taskId: task.id,
        },
        type: 'user',
      };
      relevantValues.push(newValue);
      relevantValueMap[userId] = newValue;
    }
  });

  return {
    columns: relevantColumns,
    values: relevantValues,
    inferredDateIso: gridDateIso || (tasks[0] ? getTaskISODate(tasks[0]) : null),
  };
}
